import { motion } from "framer-motion";
import React from "react";
import { CalendarEvent, EventCategory } from "../types/calendar.types";
import { getColorClasses } from "../utils/colorUtils";

interface DayEventsProps {
  date: Date;
  events: CalendarEvent[];
  categories: EventCategory[];
  onEventClick: (event: CalendarEvent) => void;
}

const DayEvents: React.FC<DayEventsProps> = ({
  date,
  events,
  categories,
  onEventClick,
}) => {
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  const formatTime = (timestamp: any): string => {
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }).format(date);
  };

  const getCategoryColor = (categoryId: string): string => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category?.color || "gray";
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
        {formatDate(date)}
      </h2>
      {events.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">
          No events for this day
        </p>
      ) : (
        <div className="space-y-2">
          {events.map((event) => {
            const color = getCategoryColor(event.categoryId);

            return (
              <motion.div
                key={event.id}
                className={`p-3 rounded-lg ${getColorClasses(color).bg} ${getColorClasses(color).bgDark} cursor-pointer`}
                onClick={() => onEventClick(event)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <h3
                  className={`${getColorClasses(color).text} ${getColorClasses(color).textDark} font-medium`}
                >
                  {event.title}
                </h3>
                <div className="flex justify-between items-center mt-1">
                  <span
                    className={`text-xs ${getColorClasses(color).textLight} ${getColorClasses(color).textLightDark}`}
                  >
                    {formatTime(event.startDate)}
                    {event.endDate && ` - ${formatTime(event.endDate)}`}
                  </span>
                  <span
                    className={`text-xs ${getColorClasses(color).textLight} ${getColorClasses(color).textLightDark}`}
                  >
                    {
                      categories.find((cat) => cat.id === event.categoryId)
                        ?.name
                    }
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DayEvents;
